import * as R from 'ramda';
import * as RA from 'ramda-adjunct';

import { WhitelabelConfigs } from 'reactApp/modules/WhiteLabelModule/WhiteLabelConfigs';

const getFormErrors = (
    errorResponseBody: McsError | McsError[] | Record<string, string> = {},
    errorTexts?: any,
) => {
    const shouldCheckFieldTexts = Boolean(errorTexts);

    const errors = ([] as (McsError | Record<string, string>)[])
        .concat(errorResponseBody)
        .filter(Boolean);

    const fieldErrors = {};
    const commonErrors: string[] = [];

    errors.forEach((errorObj) => {
        const error = _parseError(errorObj.error);
        const key = errorObj.key;
        const hasKey = key != null;
        let goesToFieldTexts = hasKey;
        if (hasKey && shouldCheckFieldTexts) {
            const hasSuchFieldInTexts =
                key != null && errorTexts.fieldTexts[key] != null;
            const hasSuchErrorInCommonTexts =
                errorTexts.commonTexts[error] != null;
            if (!hasSuchFieldInTexts && hasSuchErrorInCommonTexts) {
                goesToFieldTexts = false;
            }
        }

        if (key != null && goesToFieldTexts) {
            fieldErrors[key] = error;
        } else if (error) {
            commonErrors.push(error);
        }
    });

    if (!commonErrors.length && !Object.keys(fieldErrors).length) {
        commonErrors.push('default');
    }

    return {
        fieldErrors,
        commonErrors,
    };
};

const _parseError = (error) => {
    return (error || '').toLowerCase();
};

const getCommonErrors = (errorResponseBody) => {
    const { commonErrors } = getFormErrors(errorResponseBody);
    if (!commonErrors.length) {
        return ['default'];
    }
    return commonErrors;
};

const getCommonErrorMessages = ({ errors, texts }) => {
    return getErrorMessages({ errors, texts });
};

const getFieldErrorMessages = ({ errors, texts }) => {
    return R.mapObjIndexed((error, fieldKey) => {
        const fieldTexts = texts[fieldKey] || {};
        if (fieldTexts.default == null && texts.default != null) {
            fieldTexts.default = texts.default;
        }

        return getErrorMessages({ errors: [error], texts: fieldTexts })[0];
    })(errors);
};

const getErrorMessages = ({ errors, texts }) => {
    return errors.map((error) => {
        return texts[error] || texts.default;
    });
};

const getFormErrorMessages = ({
    fieldErrors,
    commonErrors,
    fieldTexts,
    commonTexts,
}) => {
    const { fieldErrorsFiltered, excludingKeys } = handleUnknownFieldMessages({
        fieldErrors,
        fieldTexts,
    });

    const commonMessages = getCommonErrorMessages({
        errors: commonErrors,
        texts: commonTexts,
    });

    const fieldMessages = getFieldErrorMessages({
        errors: fieldErrorsFiltered,
        texts: fieldTexts,
    });

    if (excludingKeys.length && !commonMessages.length) {
        commonMessages.push(commonTexts.default);
    }

    return { commonMessages, fieldMessages };
};

const handleUnknownFieldMessages = ({ fieldErrors, fieldTexts }) => {
    const excludingKeys = R.keys(fieldErrors).filter(
        (field) => !fieldTexts[field],
    );
    const fieldErrorsFiltered = RA.omitBy(
        (val, key) => excludingKeys.includes(key),
        fieldErrors,
    );
    return {
        excludingKeys,
        fieldErrorsFiltered,
    };
};

export type McsErrorResponse = {
    body: McsError | McsError[];
};
export type McsError = {
    error: string;
    key?: string;
    message?: string;
    value?: string;
};

const getFormMessagesFromResponse = (
    responseOrError: McsErrorResponse | McsError,
    { fieldTexts, commonTexts },
    { captchaFieldKey = 'captcha', noCaptchaUrlHandling = false } = {},
) => {
    const errorResponseBody =
        'body' in responseOrError ? responseOrError.body : responseOrError;
    const { fieldErrors, commonErrors } = McsErrors.getFormErrors(
        errorResponseBody,
        {
            fieldTexts,
            commonTexts,
        },
    );
    const errorMessages = getFormErrorMessages({
        fieldErrors,
        commonErrors,
        fieldTexts,
        commonTexts,
    });
    if (!noCaptchaUrlHandling) {
        const captchaMessage = errorMessages.fieldMessages[captchaFieldKey];
        const providedCaptchaMessages = R.values(fieldTexts[captchaFieldKey]);

        if (
            'value' in errorResponseBody &&
            errorResponseBody.value &&
            captchaMessage &&
            providedCaptchaMessages.includes(captchaMessage)
        ) {
            errorMessages.fieldMessages[
                captchaFieldKey
            ] += ` | ${errorResponseBody.value}`;
        }
    }
    return errorMessages;
};

const getCommonMessagesFromResponse = (response, texts) => {
    const errors = McsErrors.getCommonErrors(response.body);
    return getCommonErrorMessages({
        errors,
        texts,
    });
};

// its LinkCatalog.getHelpLink copy-paste, because circular dependency
function getHelpLink() {
    const whitelabelConfig =
        WhitelabelConfigs[process.env.WHITELABEL_NAME as any];
    return (
        whitelabelConfig?.support_contacts?.help_link ||
        'https://mcs.mail.ru/help/infra'
    );
}

const TRY_LATER_HTML = `Попробуйте позже или обратитесь в <a href=${getHelpLink()} rel="noopener" target="_blank">службу поддержки</a>.`;

const CALL_SUPPORT_HTML = `Пожалуйста, обратитесь в <a href=${getHelpLink()} rel="noopener" target="_blank">службу поддержки</a>.`;

export type ErrorMessages<T = any> = {
    commonMessages: string[];
    fieldMessages: Partial<Record<keyof T, React.ReactNode>>;
};

const handleErrorMessagesInForm = ({
    errorMessages,
    formikParams: { errors, setErrors },
    setCaptchaUrlToState,
    captchaFieldKey = 'captcha',
}: {
    errorMessages: ErrorMessages;
    formikParams: { errors: any; setErrors: any };
    setCaptchaUrlToState?: any;
    captchaFieldKey?: string;
}) => {
    const gotInvalidErrorMessages =
        !errorMessages.fieldMessages || !errorMessages.commonMessages;
    if (gotInvalidErrorMessages) {
        errorMessages = {
            commonMessages: [
                `Произошла непредвиденная ошибка. ${TRY_LATER_HTML}`,
            ],
            fieldMessages: {},
        };
    }

    if (setCaptchaUrlToState) {
        const {
            errorMessages: handledErrorMessages,
            captchaUrl,
        } = McsErrors.handleErrorMessagesWithCaptchaUrl(errorMessages, {
            captchaFieldKey,
        });

        if (captchaUrl) {
            setCaptchaUrlToState(`${captchaUrl}?${Date.now()}`);
        } else {
            setCaptchaUrlToState();
        }

        errorMessages = handledErrorMessages;
    }

    const { fieldMessages, commonMessages } = errorMessages;

    setErrors({
        ...errors,
        ...fieldMessages,
        commonErrorMessages: commonMessages.length ? commonMessages : undefined,
    });
};

const handleErrorMessagesWithCaptchaUrl = (
    errorMessages,
    { captchaFieldKey = 'captcha' } = {},
) => {
    const captchaMessage = errorMessages.fieldMessages[captchaFieldKey];
    let captchaUrl = '';
    if (captchaMessage) {
        const [message, url] = captchaMessage.split(' | ');
        captchaUrl = url;
        errorMessages.fieldMessages[captchaFieldKey] = message;
    }

    return { errorMessages, captchaUrl };
};

const transformErrorResponseKey = (error, transformations) => {
    const keys = Object.keys(transformations);

    if (error && keys.includes(error.key)) {
        return {
            ...error,
            body: {
                ...error,
                key: transformations[error.key],
            },
        };
    }
    return error;
};

export type WrapperErrorHandler<
    T extends (...args: any) => any = any
> = (params: {
    error: any;
    errorResponse: Response;
    values: Parameters<T>[0];
}) => ErrorMessages | any;

// eslint-disable-next-line sonarjs/cognitive-complexity
function wrapActionInErrorHandler<T extends (...args: any[]) => any>(
    func: T,
    handleError: WrapperErrorHandler<T>,
    {
        notAnAction,
        isEditAction,
        getValuesFromArgs = (args: any[]) => args[isEditAction ? 1 : 0],
        metrics: metricsConfig,
        sendMetrics = () => {},
        redux,
    }: {
        notAnAction?: boolean; // for redux only
        redux?: boolean;
        isEditAction?: boolean;
        getValuesFromArgs?: (args: any[]) => any;
        metrics?: {
            initial: string;
            success: string;
            error: string;
        };
        sendMetrics?: (event: any) => void;
    } = {},
) {
    if (!redux && notAnAction) {
        throw '"notAnAction" param is for redux mode only';
    }
    const sendMetric = (action?: string, error?: any) => {
        if (action) {
            sendMetrics({
                action,
                error,
            });
        }
    };
    let dispatch = (f) => f;
    const actionWrapper = (async (...args: Parameters<T>) => {
        try {
            sendMetric(metricsConfig?.initial);
            const result =
                redux && !notAnAction
                    ? await dispatch(func(...args))
                    : await func(...args);
            sendMetric(metricsConfig?.success);
            return result;
        } catch (error) {
            console.error(error);
            const errorBody =
                error instanceof Response
                    ? await error.json()
                    : error.responseJSON || error;
            const result = handleError({
                errorResponse: error,
                error: errorBody,
                values: getValuesFromArgs(args),
            });
            const handledErrors =
                typeof result !== 'string' && 'allErrorsKnown' in result
                    ? result.handledErrors
                    : result;
            if (!result.allErrorsKnown) {
                sendMetric(metricsConfig?.error, errorBody);
            }
            throw handledErrors;
        }
    }) as typeof func;
    if (redux) {
        return ((...args: Parameters<T>) => async (dispatchReal) => {
            dispatch = dispatchReal;
            return actionWrapper(...args);
        }) as typeof func;
    }
    return actionWrapper as typeof func;
}

function cleanFieldMessages({
    errorMessages,
    values,
    modifier,
}: {
    errorMessages: ErrorMessages;
    values: any;
    modifier?(params: { value?: any; error: any; key: string }): string;
}) {
    const fieldMessages = { ...errorMessages.fieldMessages };
    const commonMessages = [...errorMessages.commonMessages];
    Object.entries(fieldMessages).forEach(([errKey, errValue]) => {
        const value = values?.[errKey];

        if (modifier) {
            if (Array.isArray(errValue)) {
                errValue = errValue.map((item, index) =>
                    modifier({
                        error: item,
                        key: errKey,
                        value: value?.[index],
                    }),
                );
            } else {
                errValue = modifier({
                    error: errValue,
                    key: errKey,
                    value,
                });
            }

            fieldMessages[errKey] = errValue;
        }

        if (
            !values.hasOwnProperty(errKey) ||
            (!Array.isArray(errValue) && Array.isArray(value))
        ) {
            commonMessages.push(
                typeof errValue === 'string'
                    ? errValue
                    : `${errKey}: ${JSON.stringify(errValue)}`,
            );
            delete fieldMessages[errKey];
        } else if (Array.isArray(errValue) && !Array.isArray(value)) {
            fieldMessages[errKey] = errValue.join('\n');
        }
    });
    return {
        fieldMessages,
        commonMessages,
    };
}

const CAPTCHA_ERROR_MESSAGES = {
    argument_required: 'Введите код с картинки',
    argument_invalid: 'Неверный код с картинки',
    too_many_requests: 'Введите код с картинки',
};

export const McsErrors = {
    getFormErrors,
    getCommonErrors,
    getCommonErrorMessages,
    getFieldErrorMessages,
    getFormErrorMessages,
    getFormMessagesFromResponse,
    getCommonMessagesFromResponse,
    TRY_LATER_HTML,
    CALL_SUPPORT_HTML,
    handleErrorMessagesInForm,
    handleErrorMessagesWithCaptchaUrl,
    CAPTCHA_ERROR_MESSAGES,
    transformErrorResponseKey,
    wrapActionInErrorHandler,
    cleanFieldMessages,
};
